import { createApp } from 'vue'

import VueTippy, { Tippy as TippyComponent } from 'vue-tippy'
import 'tippy.js/dist/tippy.css'

import * as Elements from '@/components/elements'

import Notification from '../components/elements/Notification.vue'

function buildGlobalComponents(): Array<[string, any]> {
  const registrations = new Map<string, any>()

  for (const [exportName, component] of Object.entries(Elements as Record<string, any>)) {
    if (!component || (typeof component !== 'object' && typeof component !== 'function')) {
      continue
    }

    const names = new Set<string>()
    const componentName = component?.name

    // Skip the generic wrapper name that defineAsyncComponent() produces — it
    // would register every lazy component under the same 'AsyncComponentWrapper'
    // key and collide. The exportName path below still registers the real name.
    if (
      typeof componentName === 'string'
      && componentName.length
      && componentName !== 'AsyncComponentWrapper'
    ) {
      names.add(componentName)
    }

    if (/^O[A-Z]/.test(exportName)) {
      names.add(exportName)
    }

    for (const name of names) {
      if (!registrations.has(name)) {
        registrations.set(name, component)
      }
    }
  }

  return [...registrations.entries()]
}

const globalComponents = buildGlobalComponents()

export default defineNuxtPlugin((nuxtApp) => {
  // Register vue-tippy plugin and component
  nuxtApp.vueApp.use(VueTippy, {
    defaultProps: {
      zIndex: 85000,
      popperOptions: {
        strategy: 'fixed',
      },
    },
  })
  nuxtApp.vueApp.component('Tippy', TippyComponent)

  // Register global components
  for (const [name, component] of globalComponents) {
    nuxtApp.vueApp.component(name, component)
  }

  // Create Notification singleton mounted to a detached container (client-side only)
  if (import.meta.client) {
    const container = document.createElement('div')
    container.id = 'notification-root'
    document.body.appendChild(container)

    const notificationApp = createApp(Notification)

    // Share the parent app's global config so i18n, router, etc. are available
    notificationApp.config.globalProperties = nuxtApp.vueApp.config.globalProperties

    // Re-register global components so Notification can use o-button, o-icon, etc.
    for (const [name, component] of globalComponents) {
      notificationApp.component(name, component)
    }

    const instance = notificationApp.mount(container)

    nuxtApp.provide('notification', instance)
  }
})
